import { useEffect, useRef } from 'react';
import { Field, useForm } from 'react-final-form';
import { getValidationFunction } from '@/formbuilder/formbuilder.utilities';
import type { ValidatingFormComponent } from '@/formbuilder/formBuilder.types';

/**
 * The values a hidden field can carry; consumers narrow them in their validation function. `undefined` is included
 * because the field's value is optional and react-final-form validates the field before a value is ever set.
 */
export type HiddenFormValue = string | boolean | undefined;

export interface HiddenIF extends ValidatingFormComponent<HiddenFormValue> {
  component: 'HiddenFormComponent';
}

/**
 * HiddenFormComponent is a form component that doesn't render anything but can still
 * participate in form validation. Useful for validating external state that isn't
 * directly controlled by a form input.
 */
export const HiddenFormComponent = (props: HiddenIF) => {
  const { name, value, validation, extendValidation, required } = props;
  const form = useForm();
  const isInitialMount = useRef(true);

  const defaultValidation = (fieldValue: HiddenFormValue): boolean => {
    return !!(required && !fieldValue);
  };

  const appliedValidation = getValidationFunction(defaultValidation, extendValidation, validation);

  // Sync external value to form field, but skip initial mount to avoid marking field as modified
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
    form.change(name, value);
  }, [form, name, value]);

  return <Field name={name} initialValue={value} validate={appliedValidation} render={() => null} />;
};
